import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/db8vgpokk00l6eltrn80";

interface FormApiResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

const values = [
  {
    icon: "ri-compasses-2-line",
    title: "专注仙侠影视内容",
    desc: "围绕仙剑奇侠传及经典仙侠剧，聚合剧集资料、剧情与角色内容。",
  },
  {
    icon: "ri-book-shelf-line",
    title: "结构清晰的资料库",
    desc: "按年代、类型、地区多维组织剧集，方便快速检索与筛选。",
  },
  {
    icon: "ri-user-heart-line",
    title: "为剧迷而建",
    desc: "从粉丝的观看与了解需求出发，提供友好的浏览与收藏体验。",
  },
];

export default function About() {
  usePageMeta(
    "关于本站 - 仙剑奇侠传影视站",
    "了解仙剑奇侠传影视站的定位、内容范围、版权与免责声明，并可直接留言与我们交流你的建议与想法。",
    "关于仙剑奇侠传影视站,站点介绍,版权声明,联系方式"
  );

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("company_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    formData.delete("company_alt");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string") body.append(key, value);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: FormApiResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormApiResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim()
            ? serverMsg
            : "提交失败，请稍后重试。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试。");
    }
  };

  return (
    <div className="w-full">
      <div className="w-full px-4 md:px-8 pt-6 md:pt-8">
        <Breadcrumb items={[{ label: "关于本站" }]} />
      </div>

      <section className="w-full px-4 md:px-8 pt-6 pb-10 md:pb-14">
        <h1 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-950">
          关于本站
        </h1>
        <p className="mt-4 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
          仙剑奇侠传影视站是一个以仙剑奇侠传及经典仙侠影视作品为主题的展示与资讯站点。
          我们围绕剧集资料、剧情介绍、角色分析与最新资讯，为仙侠剧爱好者提供清晰、友好的内容浏览体验，
          陪伴每一位观众重温心中的那份仙侠情怀。
        </p>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {values.map((item) => (
            <div
              key={item.title}
              className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
            >
              <span className="w-11 h-11 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center text-xl">
                <i className={item.icon}></i>
              </span>
              <h2 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                {item.title}
              </h2>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 pb-10 md:pb-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
              <h2 className="font-heading text-xl font-semibold text-foreground-950 flex items-center gap-3">
                <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
                版权与免责声明
              </h2>
              <div className="mt-5 space-y-4 text-sm text-foreground-700 leading-relaxed">
                <p>
                  本站为面向仙侠剧爱好者的内容展示站点，站内所展示的剧集名称、简介、角色介绍等文字内容均为
                  用于演示与介绍目的的整理资料。
                </p>
                <p>
                  剧集相关的海报、剧照、角色形象等素材仅用于内容介绍与评述，其版权归各自权利方所有。
                  如相关权利方认为内容存在不当使用，请通过下方留言入口与我们联系，我们将及时处理。
                </p>
                <p>
                  本站不提供任何影视资源的播放、下载或交易服务。播放演示页面仅为前端界面效果展示，
                  不含真实视频内容，亦不代表任何实际播放能力。
                </p>
                <p>
                  本站不含用户登录、支付、下载等功能，所展示的收藏状态仅保存在访问者的浏览器本地。
                </p>
              </div>
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="font-heading text-xl font-semibold text-foreground-950 flex items-center gap-3">
                <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
                联系方式
              </h2>
              <ul className="mt-5 flex flex-col gap-4 text-sm">
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center shrink-0">
                    <i className="ri-mail-line"></i>
                  </span>
                  <span>
                    <span className="block text-foreground-900">站务邮箱</span>
                    <span className="block text-foreground-500 mt-0.5">
                      contact@xianjian-media.example.com
                    </span>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center shrink-0">
                    <i className="ri-feedback-line"></i>
                  </span>
                  <span>
                    <span className="block text-foreground-900">内容纠错与合作</span>
                    <span className="block text-foreground-500 mt-0.5">
                      欢迎通过右侧表单留言，我们会尽快回复。
                    </span>
                  </span>
                </li>
              </ul>
              <p className="mt-6 text-xs text-foreground-400">
                提示：本站为演示站点，邮箱地址与表单仅用于功能展示。
              </p>
              <Link
                to="/category"
                className="mt-6 inline-flex items-center gap-2 h-10 px-5 rounded-md border border-background-300 text-foreground-800 hover:border-primary-400 hover:text-primary-700 text-sm whitespace-nowrap transition-colors"
              >
                去看看剧集
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8 lg:sticky lg:top-24">
              <h2 className="font-heading text-xl font-semibold text-foreground-950">
                给我们留言
              </h2>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                对本站的内容或功能有任何建议，欢迎留言告诉我们。
              </p>

              <form
                id="about-contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-6 flex flex-col gap-4"
              >
                <label className="flex flex-col gap-2">
                  <span className="text-sm text-foreground-800">您的称呼</span>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="请输入您的称呼"
                    className="h-11 px-4 text-sm rounded-md border border-background-300 bg-background-100 text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-500 focus:bg-background-50"
                  />
                </label>

                <label className="flex flex-col gap-2">
                  <span className="text-sm text-foreground-800">邮箱</span>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="用于接收我们的回复"
                    className="h-11 px-4 text-sm rounded-md border border-background-300 bg-background-100 text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-500 focus:bg-background-50"
                  />
                </label>

                <label className="flex flex-col gap-2">
                  <span className="text-sm text-foreground-800">留言内容</span>
                  <textarea
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请写下您的建议或想说的话（最多 500 字）"
                    className="px-4 py-3 text-sm rounded-md border border-background-300 bg-background-100 text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-500 focus:bg-background-50 resize-none"
                  />
                </label>

                <input
                  type="text"
                  name="company_alt"
                  className="field-offscreen-probe"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="inline-flex items-center justify-center gap-2 h-11 rounded-md bg-primary-700 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      正在发送
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>
                      发送留言
                    </>
                  )}
                </button>

                {status === "success" && (
                  <p className="flex items-start gap-2 text-sm text-secondary-800 bg-secondary-100 rounded-md px-4 py-3">
                    <i className="ri-checkbox-circle-line mt-0.5"></i>
                    感谢你的留言，我们已经收到，会尽快回复你。
                  </p>
                )}
                {status === "error" && (
                  <p className="flex items-start gap-2 text-sm text-accent-900 bg-accent-100 rounded-md px-4 py-3">
                    <i className="ri-error-warning-line mt-0.5"></i>
                    {formError || "提交失败，请稍后重试。"}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}